/**
 * Profile Page Component - User profile management and investment preferences
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  Grid,
  Avatar,
  IconButton,
  Alert,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  TrendingUp,
  Notifications,
  Analytics,
  Assessment,
  Home,
  LocationOn,
  AttachMoney,
  TrendingDown,
  TrendingFlat,
  CheckCircle,
  Warning,
  Error,
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';
// import { apiService } from '../services/api'; // TODO: Enable when implementing API calls

function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: '',
    company: '',
    investment_experience: user?.investment_experience || 'beginner',
    investment_budget_min: 100000,
    investment_budget_max: 500000,
    preferred_markets: [] as string[],
    preferred_property_types: [] as string[],
    investment_strategies: [] as string[],
    risk_tolerance: 'moderate',
  });

  const [preferences, setPreferences] = useState({
    email_alerts: true,
    sms_alerts: false,
    push_notifications: true,
    min_deal_score: 70,
    max_alerts_per_day: 10,
    alert_frequency: 'daily',
  });

  const [stats] = useState({
    totalAnalyses: 12,
    avgDealScore: 78,
    apiUsageCurrent: user?.api_usage_current || 15,
    apiUsageLimit: user?.api_usage_limit || 100,
    watchlistCount: 5,
  });

  useEffect(() => {
    loadUserStats();
  }, []);

  const loadUserStats = async () => {
    try {
      // TODO: Implement API calls to get user stats
      // const statsData = await apiService.getUserStats(user.id);
      // setStats(statsData);
    } catch (error) {
      console.error('Failed to load user stats:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await updateProfile(profileData);
      setSuccess('Profile updated successfully!');
      setEditing(false);
    } catch (error: any) {
      setError(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setProfileData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      phone: '',
      company: '',
      investment_experience: user?.investment_experience || 'beginner',
      investment_budget_min: 100000,
      investment_budget_max: 500000,
      preferred_markets: [],
      preferred_property_types: [],
      investment_strategies: [],
      risk_tolerance: 'moderate',
    });
    setEditing(false);
  };

  const togglePropertyType = (type: string) => {
    setProfileData(prev => ({
      ...prev,
      preferred_property_types: prev.preferred_property_types.includes(type)
        ? prev.preferred_property_types.filter(t => t !== type)
        : [...prev.preferred_property_types, type]
    }));
  };

  const toggleStrategy = (strategy: string) => {
    setProfileData(prev => ({
      ...prev,
      investment_strategies: prev.investment_strategies.includes(strategy)
        ? prev.investment_strategies.filter(s => s !== strategy)
        : [...prev.investment_strategies, strategy]
    }));
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Profile & Investment Preferences
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Profile Overview Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box position="relative" display="inline-block">
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: 'primary.main',
                    fontSize: '3rem',
                    mb: 2
                  }}
                >
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </Avatar>
                {editing && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 0,
                      bgcolor: 'background.paper',
                      boxShadow: 2
                    }}
                  >
                    <PhotoCamera />
                  </IconButton>
                )}
              </Box>
              
              <Typography variant="h6" gutterBottom>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {user?.email}
              </Typography>
              <Chip
                label={user?.subscription_tier?.toUpperCase() || 'FREE'}
                color="primary"
                size="small"
                sx={{ mt: 1 }}
              />

              <Divider sx={{ my: 2 }} />

              {/* Quick Stats */}
              <Box sx={{ textAlign: 'left' }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    API Usage
                  </Typography>
                  <Typography variant="body2">
                    {stats.apiUsageCurrent}/{stats.apiUsageLimit}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(stats.apiUsageCurrent / stats.apiUsageLimit) * 100}
                  sx={{ mb: 2 }}
                />

                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">
                      Total Analyses
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {stats.totalAnalyses}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">
                      Avg Deal Score
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {stats.avgDealScore}/100
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">
                      Watchlist Items
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {stats.watchlistCount}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

{/* Analysis Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <Analytics sx={{ mr: 1 }} />
              Recent Analysis Results
            </Typography>
            
            {/* Analysis Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {stats.totalAnalyses}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Total Analyses
                        </Typography>
                      </Box>
                      <Assessment sx={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {stats.avgDealScore}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Avg Deal Score
                        </Typography>
                      </Box>
                      <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          3
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          This Month
                        </Typography>
                      </Box>
                      <Home sx={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          85%
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Success Rate
                        </Typography>
                      </Box>
                      <CheckCircle sx={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Recent Property Analyses */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
              Recent Property Analyses
            </Typography>
            
            <Grid container spacing={2}>
              {/* Mock Property Analysis 1 */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          123 Oak Street, Austin TX
                        </Typography>
                        <Box display="flex" alignItems="center" mb={1}>
                          <LocationOn sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="textSecondary">
                            Central Austin, TX 78701
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <AttachMoney sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="textSecondary">
                            $450,000 Purchase Price
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        label="Score: 85" 
                        color="success" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Financial Score
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={88} 
                          sx={{ height: 8, borderRadius: 4 }}
                          color="success"
                        />
                        <Typography variant="caption" color="textSecondary">
                          88/100
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Market Score
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={82} 
                          sx={{ height: 8, borderRadius: 4 }}
                          color="info"
                        />
                        <Typography variant="caption" color="textSecondary">
                          82/100
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Risk Score
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={25} 
                          sx={{ height: 8, borderRadius: 4 }}
                          color="success"
                        />
                        <Typography variant="caption" color="textSecondary">
                          25/100 (Low)
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    <Box mt={2}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>AI Insight:</strong> Strong rental demand in Central Austin with projected 8% appreciation. Low risk due to established neighborhood.
                      </Typography>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" mt={2}>
                      <Typography variant="caption" color="textSecondary">
                        Analyzed: 2 hours ago
                      </Typography>
                      <Button size="small" variant="outlined">
                        View Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Mock Property Analysis 2 */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          456 Pine Avenue, Dallas TX
                        </Typography>
                        <Box display="flex" alignItems="center" mb={1}>
                          <LocationOn sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="textSecondary">
                            Uptown Dallas, TX 75201
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <AttachMoney sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="textSecondary">
                            $320,000 Purchase Price
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        label="Score: 72" 
                        color="warning" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Financial Score
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={75} 
                          sx={{ height: 8, borderRadius: 4 }}
                          color="warning"
                        />
                        <Typography variant="caption" color="textSecondary">
                          75/100
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Market Score
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={68} 
                          sx={{ height: 8, borderRadius: 4 }}
                          color="warning"
                        />
                        <Typography variant="caption" color="textSecondary">
                          68/100
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Risk Score
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={45} 
                          sx={{ height: 8, borderRadius: 4 }}
                          color="warning"
                        />
                        <Typography variant="caption" color="textSecondary">
                          45/100 (Moderate)
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    <Box mt={2}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>AI Insight:</strong> Moderate investment potential with stable rental market. Consider negotiating purchase price for better returns.
                      </Typography>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" mt={2}>
                      <Typography variant="caption" color="textSecondary">
                        Analyzed: 1 day ago
                      </Typography>
                      <Button size="small" variant="outlined">
                        View Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Market Insights Section */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              Latest Market Insights
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card sx={{ border: '1px solid', borderColor: 'success.main' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <TrendingUp sx={{ color: 'success.main', mr: 1 }} />
                      <Typography variant="h6" color="success.main">
                        Austin Market
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Strong growth trajectory with 12% YoY appreciation. High demand for rental properties in tech corridors.
                    </Typography>
                    <Chip size="small" label="Hot Market" color="success" variant="outlined" />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card sx={{ border: '1px solid', borderColor: 'warning.main' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <TrendingFlat sx={{ color: 'warning.main', mr: 1 }} />
                      <Typography variant="h6" color="warning.main">
                        Dallas Market
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Stable market conditions with 6% YoY growth. Good fundamentals but slower appreciation rates.
                    </Typography>
                    <Chip size="small" label="Stable" color="warning" variant="outlined" />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card sx={{ border: '1px solid', borderColor: 'error.main' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Warning sx={{ color: 'error.main', mr: 1 }} />
                      <Typography variant="h6" color="error.main">
                        Houston Market
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Market cooling with 2% YoY growth. Oversupply concerns in some neighborhoods. Exercise caution.
                    </Typography>
                    <Chip size="small" label="Cooling" color="error" variant="outlined" />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Box display="flex" justifyContent="center" mt={3}>
              <Button variant="contained" size="large" startIcon={<Analytics />}>
                Run New Analysis
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Main Profile Form */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Personal Information
              </Typography>
              {!editing ? (
                <Button
                  startIcon={<Edit />}
                  onClick={() => setEditing(true)}
                  variant="outlined"
                >
                  Edit Profile
                </Button>
              ) : (
                <Box>
                  <Button
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    startIcon={<Save />}
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={profileData.first_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                  disabled={!editing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={profileData.last_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, last_name: e.target.value }))}
                  disabled={!editing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  disabled={!editing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={profileData.phone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                  disabled={!editing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company (Optional)"
                  value={profileData.company}
                  onChange={(e) => setProfileData(prev => ({ ...prev, company: e.target.value }))}
                  disabled={!editing}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Investment Preferences */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <TrendingUp sx={{ mr: 1 }} />
              Investment Preferences
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Investment Experience</InputLabel>
                  <Select
                    value={profileData.investment_experience}
                    onChange={(e) => setProfileData(prev => ({ ...prev, investment_experience: e.target.value }))}
                    disabled={!editing}
                  >
                    <MenuItem value="beginner">Beginner (0-2 years)</MenuItem>
                    <MenuItem value="intermediate">Intermediate (3-7 years)</MenuItem>
                    <MenuItem value="advanced">Advanced (8+ years)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Risk Tolerance</InputLabel>
                  <Select
                    value={profileData.risk_tolerance}
                    onChange={(e) => setProfileData(prev => ({ ...prev, risk_tolerance: e.target.value }))}
                    disabled={!editing}
                  >
                    <MenuItem value="conservative">Conservative</MenuItem>
                    <MenuItem value="moderate">Moderate</MenuItem>
                    <MenuItem value="aggressive">Aggressive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>Investment Budget Range</Typography>
                <Box sx={{ px: 2 }}>
                  <Slider
                    value={[profileData.investment_budget_min, profileData.investment_budget_max]}
                    onChange={(e, value) => {
                      const [min, max] = value as number[];
                      setProfileData(prev => ({
                        ...prev,
                        investment_budget_min: min,
                        investment_budget_max: max
                      }));
                    }}
                    valueLabelDisplay="auto"
                    min={50000}
                    max={2000000}
                    step={25000}
                    disabled={!editing}
                    valueLabelFormat={(value) => `$${(value / 1000).toFixed(0)}K`}
                  />
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="body2">
                      ${(profileData.investment_budget_min / 1000).toFixed(0)}K
                    </Typography>
                    <Typography variant="body2">
                      ${(profileData.investment_budget_max / 1000).toFixed(0)}K
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom sx={{ fontWeight: 600 }}>
                  Preferred Property Types
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {['Single Family', 'Multi-Family', 'Condos', 'Townhouses', 'Commercial', 'Land'].map((type) => (
                    <Chip
                      key={type}
                      label={type}
                      onClick={() => editing && togglePropertyType(type)}
                      color={profileData.preferred_property_types.includes(type) ? 'primary' : 'default'}
                      variant={profileData.preferred_property_types.includes(type) ? 'filled' : 'outlined'}
                      disabled={!editing}
                    />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom sx={{ fontWeight: 600 }}>
                  Investment Strategies
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    { value: 'buy_and_hold', label: 'Buy & Hold' },
                    { value: 'flip', label: 'Fix & Flip' },
                    { value: 'brrrr', label: 'BRRRR' },
                    { value: 'wholesale', label: 'Wholesale' }
                  ].map(({ value, label }) => (
                    <Chip
                      key={value}
                      label={label}
                      onClick={() => editing && toggleStrategy(value)}
                      color={profileData.investment_strategies.includes(value) ? 'primary' : 'default'}
                      variant={profileData.investment_strategies.includes(value) ? 'filled' : 'outlined'}
                      disabled={!editing}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Alert Preferences */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <Notifications sx={{ mr: 1 }} />
              Alert Preferences
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email_alerts}
                      onChange={(e) => setPreferences(prev => ({ ...prev, email_alerts: e.target.checked }))}
                      disabled={!editing}
                    />
                  }
                  label="Email Alerts"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.sms_alerts}
                      onChange={(e) => setPreferences(prev => ({ ...prev, sms_alerts: e.target.checked }))}
                      disabled={!editing}
                    />
                  }
                  label="SMS Alerts"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.push_notifications}
                      onChange={(e) => setPreferences(prev => ({ ...prev, push_notifications: e.target.checked }))}
                      disabled={!editing}
                    />
                  }
                  label="Push Notifications"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography gutterBottom>Minimum Deal Score for Alerts</Typography>
                <Slider
                  value={preferences.min_deal_score}
                  onChange={(e, value) => setPreferences(prev => ({ ...prev, min_deal_score: value as number }))}
                  min={50}
                  max={95}
                  step={5}
                  valueLabelDisplay="auto"
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Alert Frequency</InputLabel>
                  <Select
                    value={preferences.alert_frequency}
                    onChange={(e) => setPreferences(prev => ({ ...prev, alert_frequency: e.target.value }))}
                    disabled={!editing}
                  >
                    <MenuItem value="immediate">Immediate</MenuItem>
                    <MenuItem value="hourly">Hourly</MenuItem>
                    <MenuItem value="daily">Daily Digest</MenuItem>
                    <MenuItem value="weekly">Weekly Summary</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ProfilePage;
